<template>
  <div
    class="bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full transition-all duration-200 relative"
    :class="collapsed ? 'w-12' : 'w-64'"
  >
    <!-- Collapse Toggle -->
    <button
      type="button"
      @click="$emit('toggle-sidebar')"
      class="absolute -right-2.5 top-4 w-5 h-5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full flex items-center justify-center transition-colors z-50 shadow"
    >
      <i
        class="fas text-xs transition-transform duration-200"
        :class="collapsed ? 'fa-chevron-right' : 'fa-chevron-left'"
      ></i>
    </button>

    <!-- Header -->
    <div class="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between">
        <h2
          v-if="!collapsed"
          class="text-sm font-semibold text-gray-900 dark:text-white"
        >
          Rota Detayları
        </h2>
        <div v-else class="w-6 h-6 flex items-center justify-center mx-auto">
          <i class="fas fa-route text-gray-500 text-xs"></i>
        </div>
        <button
          v-if="!collapsed"
          type="button"
          @click="$emit('close')"
          class="w-6 h-6 rounded hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-colors"
        >
          <i class="fas fa-times text-gray-400 text-xs"></i>
        </button>
      </div>
    </div>

    <!-- Content Area - Scrollable -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="!collapsed" class="p-3 space-y-3">
        <!-- Route Stats Cards -->
        <RouteStatsCard
          :totalKilometer="totalKilometer"
          :totalMinutes="totalMinutes"
          :totalCost="totalCost"
          :fuelPrice="fuelPrice"
          @show-toll-details="$emit('show-modal')"
        />

        <!-- Route Settings -->
        <div class="border-t border-gray-200 dark:border-gray-700 pt-3">
          <RouteSettingsPanel
            :routeType="routeType"
            :vehicleTypeId="vehicleTypeId"
            :isToll="isToll"
            :isExplosive="isExplosive"
            :isShip="isShip"
            :vehicleTypes="vehicleTypes"
            @update:routeType="$emit('update:routeType', $event)"
            @update:vehicleTypeId="$emit('update:vehicleTypeId', $event)"
            @update:isToll="$emit('update:isToll', $event)"
            @update:isExplosive="$emit('update:isExplosive', $event)"
            @update:isShip="$emit('update:isShip', $event)"
            @update="$emit('settings-updated')"
          />
        </div>

        <!-- Map Tools -->
        <div class="border-t border-gray-200 dark:border-gray-700 pt-3">
          <button
            type="button"
            @click="$emit('add-rectangle')"
            class="w-full text-xs font-medium py-2 px-3 rounded transition-colors flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white"
          >
            <i class="fas fa-plus mr-1.5 text-xs"></i>
            Yasaklı Alan Ekle
          </button>
        </div>
      </div>

      <!-- Collapsed State - Icon Bar -->
      <div v-else class="p-2 space-y-3 flex flex-col items-center">
        <!-- Distance Icon -->
        <div class="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center group relative">
          <i class="fas fa-route text-gray-500 text-xs"></i>
          <div class="absolute left-10 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
            {{ totalKilometer }}km
          </div>
        </div>

        <!-- Time Icon -->
        <div class="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center group relative">
          <i class="fas fa-clock text-gray-500 text-xs"></i>
          <div class="absolute left-10 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
            {{ getTimeDetail(totalMinutes) }}
          </div>
        </div>

        <!-- Cost Icon -->
        <div class="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center group relative">
          <i class="fas fa-lira-sign text-gray-500 text-xs"></i>
          <div class="absolute left-10 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
            {{ Money(totalCost + fuelPrice) }}₺
          </div>
        </div>

        <!-- Recalculate Icon -->
        <button
          type="button"
          @click="$emit('recalculate')"
          class="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded flex items-center justify-center group relative transition-colors"
        >
          <i class="fas fa-sync text-white text-xs"></i>
          <div class="absolute left-10 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
            Hesapla
          </div>
        </button>
      </div>
    </div>

    <!-- Action Buttons (Only in expanded state) -->
    <div v-if="!collapsed" class="mt-auto p-3 space-y-2 border-t border-gray-200 dark:border-gray-700">
      <button
        type="button"
        @click="$emit('recalculate')"
        class="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded transition-colors"
      >
        <i class="fas fa-route mr-1.5"></i>
        Rotayı Hesapla
      </button>

      <button
        v-if="hasWaypoints"
        type="button"
        @click="$emit('undo-waypoint')"
        class="w-full bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium py-2 px-3 rounded transition-colors"
      >
        <i class="fas fa-undo mr-1.5"></i>
        Son Nokta Geri Al
      </button>

      <button
        type="button"
        @click="$emit('close')"
        class="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium py-2 px-3 rounded transition-colors"
      >
        <i class="fas fa-times mr-1.5"></i>
        Kapat
      </button>
    </div>
  </div>
</template>

<script>
import RouteStatsCard from './RouteStatsCard.vue'
import RouteSettingsPanel from './RouteSettingsPanel.vue'

export default {
  name: 'RouteDetailsSidebar',
  components: {
    RouteStatsCard,
    RouteSettingsPanel
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false
    },
    totalKilometer: {
      type: Number,
      default: 0
    },
    totalMinutes: {
      type: Number,
      default: 0
    },
    totalCost: {
      type: Number,
      default: 0
    },
    fuelPrice: {
      type: Number,
      default: 0
    },
    routeType: {
      type: Number,
      default: 1
    },
    vehicleTypeId: {
      type: Number,
      default: 5
    },
    isToll: {
      type: Number,
      default: 1
    },
    isExplosive: {
      type: Number,
      default: 0
    },
    isShip: {
      type: Number,
      default: 0
    },
    vehicleTypes: {
      type: Array,
      default: () => []
    },
    rectangles: {
      type: Array,
      default: () => []
    },
    hasWaypoints: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'toggle-sidebar',
    'close',
    'settings-updated',
    'add-rectangle',
    'remove-rectangle',
    'recalculate',
    'undo-waypoint',
    'show-modal',
    'update:routeType',
    'update:vehicleTypeId',
    'update:isToll',
    'update:isExplosive',
    'update:isShip'
  ],
  methods: {
    Money(value) {
      return Number(value).toFixed(2);
    },
    getTimeDetail(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return `${hours}s ${mins}dk`;
    }
  }
}
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}
</style>